<script lang="ts" setup>
import VSpinner from '@shared/component/VSpinner/VSpinner.vue';
import { anchorLinks, isEmpty } from '@tools/helpers';
import { useFocus } from '@vueuse/core';
import { computed, toRefs, watch, useTemplateRef } from 'vue';

export interface TextFieldProps {
  ariaLabel?: string;
  autoGrow?: boolean;
  disabled?: boolean;
  focusOnMount?: boolean;
  hasError?: boolean;
  id?: string;
  isHtml?: boolean;
  loading?: boolean;
  maxLength?: number;
  path?: string;
  placeholder?: string;
  readonly?: boolean;
  rows?: number;
  value?: string | null;
}

const props = withDefaults(defineProps<TextFieldProps>(), {
  loading: false,
  readonly: false,
  disabled: false,
  isHtml: false,
  hasError: false,
  rows: 1,
  focusOnMount: false,
});

const emit = defineEmits<{
  updateField: [valuePath: { path?: string; value?: string }];
  'update:value': [value?: string];
  blur: [];
}>();

const model = defineModel<string>();
const { focusOnMount, value } = toRefs(props);

const root = useTemplateRef<HTMLElement>('root');
const target = useTemplateRef<HTMLTextAreaElement>('target');

const { focused } = useFocus(target, { initialValue: focusOnMount.value });

defineExpose({
  focus: () => (focused.value = true),
});

watch(
  () => props.value,
  (newValue) => (model.value = newValue ?? ''),
  {
    immediate: !!value.value,
  },
);

const rows = computed(() => (props.rows > 0 ? props.rows : 1));
const valueLength = computed(() => model.value?.length ?? 0);

function handleBlur() {
  emit('updateField', { path: props.path, value: model.value });
  emit('update:value', model.value);
  emit('blur');
}

function setAnchorLinks(modelValue: string | number | string[] | undefined) {
  return anchorLinks(modelValue as string);
}

watch(
  [model, root],
  ([newModel, newRoot]) =>
    newRoot && props.autoGrow && (newRoot.dataset.shadowValue = newModel ?? ''),
);
</script>

<template>
  <div
    v-if="!readonly"
    class="input-wrapper group/textarea relative! flex-1!"
    :class="[
      {
        'auto-grow grid! after:invisible! after:col-start-1! after:col-end-2! after:row-start-1! after:row-end-2! after:border! after:p-[.5625em_.75em]! after:font-sans! after:leading-normal! after:overflow-wrap-anywhere! after:whitespace-pre-wrap!':
          props.autoGrow,
        'border-red-500!': hasError,
      },
    ]"
    ref="root"
  >
    <textarea
      v-model="model"
      class="styled-input peer h-auto!"
      :class="[
        {
          'col-start-1! col-end-2! row-start-1! row-end-2! resize-none! overflow-hidden!':
            props.autoGrow,
          'pt-8!': props.maxLength,
          filled: !isEmpty(model),
          'border-red-500!': hasError,
        },
      ]"
      ref="target"
      :id="props.id"
      :aria-label="props.ariaLabel"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :rows="rows"
      :maxlength="props.maxLength"
      @blur="handleBlur"
      data-testid="text-field-input"
    ></textarea>
    <VSpinner v-if="props.loading" />

    <small
      v-if="props.maxLength"
      class="absolute top-0 right-0 z-1 hidden px-2 text-black group-hover:flex peer-focus:flex"
      :class="[valueLength >= props.maxLength ? 'bg-red-300' : 'bg-primary']"
    >
      {{ valueLength }}/{{ props.maxLength }} tekens
    </small>
  </div>

  <div v-else class="filled">
    <div
      data-testid="text-field-readonly-html"
      v-if="isHtml"
      v-html="model ? setAnchorLinks(model) : '-'"
    ></div>
    <div class="readonly-text wrap-break-word" data-testid="text-field-readonly-text" v-else>
      {{ model || '-' }}
    </div>
  </div>
</template>

<style scoped>
.auto-grow::after {
  /* Dit kan (momenteel) niet met Tailwind */
  content: attr(data-shadow-value) ' ';
}
</style>
